import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ASSETS, CALENDLY_URL, NAV } from "./config";
import { NavDropdown } from "./NavDropdown";
import { scrollToSection } from "./scroll";

export const BookACallLink = ({ className = "bp-btn", testId, label = "BOOK A CALL" }) => (
  <a className={className} href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid={testId}>
    {label}
  </a>
);

const MobileMenu = ({ open, onClose }) => {
  const navigate = useNavigate();
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const go = (target) => (e) => {
    e.preventDefault();
    onClose();
    scrollToSection(target, navigate);
  };

  return (
    <div id="bp-mobile-menu" className="bp-mobile-menu" hidden={!open} data-testid="mobile-menu">
      <nav aria-label="Mobile">
        {NAV.map((group) => (
          <div key={group.id} className="bp-mobile-group">
            {group.items ? (
              <>
                <p className="bp-mobile-label">{group.label}</p>
                {group.items.map((item) => (
                  <a key={item.label} href={`#${item.target}`} className="bp-mobile-link" onClick={go(item.target)} data-testid={`mobile-nav-${group.id}-${item.target}`}>
                    {item.label}
                  </a>
                ))}
              </>
            ) : group.href ? (
              <Link to={group.href} className="bp-mobile-link bp-mobile-link--top" onClick={onClose} data-testid={`mobile-nav-${group.id}`}>
                {group.label}
              </Link>
            ) : (
              <a href={`#${group.target}`} className="bp-mobile-link bp-mobile-link--top" onClick={go(group.target)} data-testid={`mobile-nav-${group.id}`}>
                {group.label}
              </a>
            )}
          </div>
        ))}
        <BookACallLink className="bp-btn bp-mobile-cta" testId="mobile-nav-book-a-call" />
      </nav>
    </div>
  );
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (target) => (e) => {
    e.preventDefault();
    scrollToSection(target, navigate);
  };

  return (
    <header className={`bp-header${scrolled ? " is-scrolled" : ""}`} data-testid="site-header">
      <div className="bp-wrap bp-header-inner">
        <Link to="/" className="bp-logo-link" aria-label="The Business Playwright — home" data-testid="header-logo-link">
          <img src={ASSETS.logo} alt="The Business Playwright logo" className="bp-logo" width="1254" height="1254" fetchPriority="high" decoding="async" />
        </Link>

        <nav className="bp-nav" aria-label="Primary">
          <ul className="bp-nav-list">
            {NAV.map((group) =>
              group.items ? (
                <NavDropdown key={group.id} group={group} />
              ) : (
                <li key={group.id} className="bp-nav-item">
                  {group.href ? (
                    <Link to={group.href} className="bp-nav-link" data-testid={`nav-${group.id}`}>{group.label}</Link>
                  ) : (
                    <a href={`#${group.target}`} className="bp-nav-link" onClick={goTo(group.target)} data-testid={`nav-${group.id}`}>{group.label}</a>
                  )}
                </li>
              )
            )}
          </ul>
          <BookACallLink className="bp-btn bp-btn--nav" testId="nav-book-a-call" />
        </nav>

        <button
          type="button"
          className={`bp-burger${menuOpen ? " is-open" : ""}`}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          aria-controls="bp-mobile-menu"
          onClick={() => setMenuOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
        >
          <span /><span />
        </button>
      </div>
      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </header>
  );
};
